import {Component, inject, input} from "@angular/core";
import {PeerPracticeSession, PeerPracticeStation, SimGroup} from "@models";
import {MatChip, MatChipSet} from "@angular/material/chips";
import {SimGroupingService, SimScenarioService, SimTrainingRoomService} from "@services";
import {derivedAsync} from "ngxtension/derived-async";
import {MatIcon} from "@angular/material/icon";
import {TranslateModule} from "@ngx-translate/core";
import {Translations} from "@translations";

@Component({
	selector: "sim-peer-practice-session-card-station-content",
	templateUrl: "./peer-practice-session-card-station-content.component.html",
	styleUrl: "./peer-practice-session-card-station-content.component.scss",
	imports: [MatIcon, TranslateModule, MatChip, MatChipSet],
})
export class PeerPracticeSessionCardStationContentComponent {
	// Inputs
	public readonly station = input.required<PeerPracticeStation>();
	public readonly index = input.required<number>();
	public readonly session = input<PeerPracticeSession | undefined>();

	// Properties
	protected readonly scenarioNames = derivedAsync<string>(() => this.computeScenarioNames(this.station()), {initialValue: ""});
	protected readonly roomName = derivedAsync<string>(() => this.computeRoomName(this.station()), {initialValue: ""});
	protected readonly groups = derivedAsync<SimGroup[]>(() => this.computeGroups(this.session()), {initialValue: []});

	// Constants
	protected readonly T = Translations;

	// Injections
	readonly #scenarioService = inject(SimScenarioService);
	readonly #roomService = inject(SimTrainingRoomService);
	readonly #groupService = inject(SimGroupingService);

	private async computeScenarioNames(station: PeerPracticeStation): Promise<string> {
		let result = "";
		const preloadedScenarios = this.station().scenarios;
		if (preloadedScenarios?.length) {
			result = preloadedScenarios.map((scenario) => scenario.name).join(", ");
		} else if (this.station().scenarioIds.length) {
			const fetchedScenarios = await this.#scenarioService.getScenarioByIds(station.scenarioIds);
			result = fetchedScenarios.map((scenario) => scenario.name).join(", ");
		}
		return result;
	}

	private async computeRoomName(station: PeerPracticeStation): Promise<string> {
		let result = "";
		if (station.room) {
			result = station.room.name;
		} else if (station.roomId) {
			const fetchedRoom = await this.#roomService.getTrainingRoomById(station.roomId);
			result = fetchedRoom.name;
		}
		return result;
	}

	private async computeGroups(peerPracticeSession?: PeerPracticeSession | undefined): Promise<SimGroup[]> {
		let result: SimGroup[] = [];
		if (peerPracticeSession?.userSubGroups.length) {
			result = peerPracticeSession.userSubGroups;
		} else if (peerPracticeSession?.userSubGroupIds.length) {
			result = await this.#groupService.getSimGroupsByIds(peerPracticeSession.userSubGroupIds);
		}
		return result;
	}
}
